// IndexedDB utility for storing GLB models
const DB_NAME = 'AvatarModelCache';
const DB_VERSION = 1;
const STORE_NAME = 'models';

class GLBCache {
  constructor() {
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[IndexedDB] Failed to open database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('[IndexedDB] Database opened successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'url' });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('[IndexedDB] Object store created');
        }
      };
    });
  }

  async getModel(url) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(url);

      request.onsuccess = () => {
        if (request.result) {
          console.log('[IndexedDB] Cache hit for:', url);
          resolve(request.result.data);
        } else {
          console.log('[IndexedDB] Cache miss for:', url);
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('[IndexedDB] Error getting model:', request.error);
        reject(request.error);
      };
    });
  }

  async saveModel(url, data) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const record = {
        url: url,
        data: data,
        timestamp: Date.now()
      };
      const request = store.put(record);

      request.onsuccess = () => {
        console.log('[IndexedDB] Model cached:', url);
        resolve();
      };

      request.onerror = () => {
        console.error('[IndexedDB] Error saving model:', request.error);
        reject(request.error);
      };
    });
  }

  async clearCache() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('[IndexedDB] Cache cleared');
        resolve();
      };

      request.onerror = () => {
        console.error('[IndexedDB] Error clearing cache:', request.error);
        reject(request.error);
      };
    });
  }

  async getCacheSize() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const records = request.result;
        let totalSize = 0;
        records.forEach(record => {
          if (record.data instanceof ArrayBuffer) {
            totalSize += record.data.byteLength;
          }
        });
        console.log('[IndexedDB] Total cache size:', (totalSize / 1024 / 1024).toFixed(2), 'MB');
        resolve(totalSize);
      };

      request.onerror = () => {
        console.error('[IndexedDB] Error getting cache size:', request.error);
        reject(request.error);
      };
    });
  }
}

// Export singleton instance
const glbCache = new GLBCache();
export default glbCache;
